import React, {
  createContext,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'
import {Lang} from 'yougile-lang'
import {Debouncer} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {Storage} from '@client/storage'
import {IconButton} from '@components/common/base/buttons'
import {TriCheckboxNew} from '@components/common/base/checkbox-new'
import {InputNew} from '@components/common/base/input-new'
import {
  TreeListWrapper,
  TwEmptyList,
  TwList,
  TwListItem,
  TwListItemTitle,
  TwSearchWrapper,
  TwTreeList
} from '@components/common/base/tree-list/containers'
import {
  findAllCollapsableNodes,
  findAllNodesIdsInBranch,
  findAndFilterListBySearch,
  findNode,
  getIsItemSelected
} from '@components/common/base/tree-list/utils'
import {Highlight} from '@components/common/highlight/highlight'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'

export interface TreeListItem {
  id: DataObjectId
  title: string
  subItems?: TreeListItem[]
}

export interface TreeListProps {
  list: TreeListItem[]
  debouncedSearch?: boolean
  selected?: DataObjectId[]
  withSearch?: boolean
  className?: string
  firstCollapsed?: boolean
  collapsedByDefault?: DataObjectId[]
  onSelect?: (items: DataObjectId[]) => void
  onCollapse?: (items?: DataObjectId[]) => void
  collapsedLocalStorageKey?: string
}

interface TreeListStore {
  props: TreeListProps
  search?: string
  setSearch: (str: SetStateAction<string>) => void
  currentList?: TreeListItem[]
  setCurrentList: (list: SetStateAction<TreeListItem[]>) => void
  selected: DataObjectId[]
  collapsed: DataObjectId[]
  onSetCollapsed: (collapsed: DataObjectId[]) => void
  onSetSelected: (selected: DataObjectId[]) => void
}

const TreeListContext = createContext({} as TreeListStore)

const debouncer = new Debouncer(600)

export const TreeList = React.memo((props: TreeListProps) => {
  const [search, setSearch] = useState('')
  const [currentList, setCurrentList] = useState<TreeListItem[]>()
  const [selected, setSelected] = useState<DataObjectId[]>([])
  const [collapsed, setCollapsed] = useState<DataObjectId[]>([])

  useEffect(() => {
    if (
      props.selected &&
      (props.selected?.length !== selected.length ||
        props.selected.every((v, i) => v === selected[i]))
    ) {
      setSelected(props.selected)
    }
  }, [props.selected])

  useEffect(() => {
    setCurrentList(props.list)
  }, [props.list])

  useEffect(() => {
    const firstItemId = props.list.length ? props.list[0].id : undefined
    const collapsedFromStorage =
      (props.collapsedLocalStorageKey &&
        (Storage.getItem(props.collapsedLocalStorageKey)?.split(
          '.'
        ) as DataObjectId[])) ||
      undefined

    if (collapsedFromStorage?.length) {
      setCollapsed(collapsedFromStorage)
    } else if (props.firstCollapsed && firstItemId && !collapsed.length) {
      setCollapsed([firstItemId])
    }
  }, [props.firstCollapsed, props.list, props.collapsedLocalStorageKey])

  useEffect(() => {
    if (props.collapsedByDefault?.length && !collapsed.length) {
      setCollapsed(props.collapsedByDefault)
    }
  }, [props.collapsedByDefault])

  const onSetCollapsed = useCallback(
    (collapsedItems: DataObjectId[]) => {
      setCollapsed(collapsedItems)

      if (props.collapsedLocalStorageKey) {
        Storage.setItem(
          props.collapsedLocalStorageKey,
          collapsedItems.join('.')
        )
      }

      if (props.onCollapse) {
        props.onCollapse(collapsedItems)
      }
    },
    [props.onCollapse]
  )

  const onSetSelected = useCallback(
    (selectedItems: DataObjectId[]) => {
      setSelected(selectedItems)
      setCurrentList(
        findAndFilterListBySearch(
          props.list,
          search.toLowerCase(),
          selectedItems
        )
      )

      if (props.onSelect) {
        props.onSelect(selectedItems)
      }
    },
    [search]
  )

  return (
    <TreeListContext.Provider
      value={{
        props,
        search,
        setSearch,
        currentList,
        setCurrentList,
        selected,
        onSetSelected,
        collapsed,
        onSetCollapsed
      }}
    >
      <TreeListBase />
    </TreeListContext.Provider>
  )
})

const TreeListBase = () => {
  const {
    props,
    search,
    currentList,
    setCurrentList,
    setSearch,
    selected,
    onSetCollapsed
  } = useContext(TreeListContext)

  const onChangeSearchString = (text: string) => {
    if (!text) {
      setSearch(text)
      setCurrentList(props.list)
      return
    }

    setSearch(text)

    debouncer.runLatest(() => {
      const newCurrentList = findAndFilterListBySearch(
        props.list,
        text.toLowerCase(),
        selected
      )

      setCurrentList(newCurrentList)
      onSetCollapsed(findAllCollapsableNodes(newCurrentList))
    })
  }

  return (
    <TwTreeList className={props.className}>
      {props.withSearch && (
        <TwSearchWrapper>
          <InputNew
            baseFocusStyle
            value={search || ''}
            icon="IconSearch"
            onChange={onChangeSearchString}
          />
        </TwSearchWrapper>
      )}

      {!!currentList?.length && (
        <WithPerfectScrollbar>
          <TreeListWrapper>
            <List list={currentList} />
          </TreeListWrapper>
        </WithPerfectScrollbar>
      )}
      {!currentList?.length && (
        <TwEmptyList>{Lang.msg.emptySearchResults}</TwEmptyList>
      )}
    </TwTreeList>
  )
}

interface ListProps {
  list: TreeListItem[]
  level?: number
}
const List = (props: ListProps) => {
  if (!props.list?.length) {
    return null
  }

  return (
    <TwList $level={props.level}>
      {props.list.map(listItem => {
        return (
          <ListItem key={listItem.id} level={props.level || 0} {...listItem} />
        )
      })}
    </TwList>
  )
}

interface ListItemProps extends TreeListItem, Pick<ListProps, 'level'> {}
const ListItem = (props: ListItemProps) => {
  const {
    props: parentProps,
    search,
    onSetCollapsed,
    collapsed,
    selected: storeSelected,
    onSetSelected
  } = useContext(TreeListContext)

  const itemIsCollapsed = collapsed?.includes(props.id) || false

  const basicNode = findNode(props.id, parentProps.list)

  const onChangeSelected = (value: boolean) => {
    const selectedItems: DataObjectId[] = !basicNode?.subItems?.length
      ? [props.id]
      : findAllNodesIdsInBranch(basicNode)

    if (value) {
      onSetSelected(Array.from(new Set([...selectedItems, ...storeSelected])))
    } else {
      onSetSelected(
        storeSelected.filter(
          storeSelectedItem => !selectedItems.includes(storeSelectedItem)
        )
      )
    }
  }

  const handleCollapse = () => {
    if (collapsed?.includes(props.id)) {
      onSetCollapsed(
        collapsed.filter(collapsedItem => collapsedItem !== props.id)
      )
    } else {
      onSetCollapsed([...(collapsed || []), props.id])
    }
  }

  const getIsSelected = (): boolean | undefined => {
    if (!storeSelected.length || !basicNode) {
      return false
    }

    return getIsItemSelected(storeSelected, basicNode)
  }

  return (
    <>
      <TwListItem>
        <TwListItemTitle>
          <TriCheckboxNew
            value={getIsSelected()}
            label={
              search ? (
                <Highlight text={props.title} highlight={search} />
              ) : (
                props.title
              )
            }
            onChange={onChangeSelected}
          />
        </TwListItemTitle>
        {!!props.subItems?.length && (
          <div>
            <IconButton
              icon={
                itemIsCollapsed
                  ? 'IconCustomChevronDown'
                  : 'IconCustomChevronUp'
              }
              onClick={handleCollapse}
              color="grey"
            />
          </div>
        )}
      </TwListItem>
      {itemIsCollapsed && !!props.subItems?.length && (
        <List
          {...props}
          list={props.subItems}
          level={(props?.level || 0) + 1}
        />
      )}
    </>
  )
}
